import {
  EmodeDataHumanized,
  ReservesDataHumanized,
  UiPoolDataProvider,
  UserReserveDataHumanized,
} from '@aave/contract-helpers';
import { UiPoolDataProvider as LegacyUiPoolDataProvider } from '@aave/contract-helpers-legacy';
import { Provider } from '@ethersproject/providers';
import { MarketDataType } from 'src/ui-config/marketsConfig';

export type UserReservesDataHumanized = {
  userReserves: UserReserveDataHumanized[];
  userEmodeCategoryId: number;
};

export class UiPoolService {
  constructor(private readonly getProvider: (chainId: number) => Provider) {}

  private async getUiPoolDataService(marketData: MarketDataType) {
    const provider = this.getProvider(marketData.chainId);
    return new UiPoolDataProvider({
      uiPoolDataProviderAddress: marketData.addresses.UI_POOL_DATA_PROVIDER,
      provider,
      chainId: marketData.chainId,
    });
  }

  private async getLegacyUiPoolDataService(marketData: MarketDataType) {
    const provider = this.getProvider(marketData.chainId);
    return new LegacyUiPoolDataProvider({
      uiPoolDataProviderAddress: marketData.addresses.UI_POOL_DATA_PROVIDER,
      provider,
      chainId: marketData.chainId,
    });
  }

  async getReservesHumanized(marketData: MarketDataType): Promise<ReservesDataHumanized> {
    if (Number(marketData.chainId) === 46630) {
      // Sward currently runs the original V3 pool layout. Its UI helper includes
      // stable-debt fields that were removed from the latest Aave markets.
      try {
        const legacyProvider = await this.getLegacyUiPoolDataService(marketData);
        const result = await legacyProvider.getReservesHumanized({
          lendingPoolAddressProvider: marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER,
        });
        return {
          ...result,
          reservesData: result.reservesData.map((reserve, originalId) => ({
            ...reserve,
            originalId,
            deficit: '0',
          })),
        } as unknown as ReservesDataHumanized;
      } catch (error) {
        console.error('Sward reserve compatibility query failed', error);
        throw error;
      }
    }

    const uiPoolDataProvider = await this.getUiPoolDataService(marketData);
    return uiPoolDataProvider.getReservesHumanized({
      lendingPoolAddressProvider: marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER,
    });
  }

  async getUserReservesHumanized(
    marketData: MarketDataType,
    user: string
  ): Promise<UserReservesDataHumanized> {
    if (Number(marketData.chainId) === 46630) {
      const legacyProvider = await this.getLegacyUiPoolDataService(marketData);
      return legacyProvider.getUserReservesHumanized({
        user,
        lendingPoolAddressProvider: marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER,
      }) as unknown as UserReservesDataHumanized;
    }

    const uiPoolDataProvider = await this.getUiPoolDataService(marketData);
    return uiPoolDataProvider.getUserReservesHumanized({
      user,
      lendingPoolAddressProvider: marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER,
    });
  }

  async getEModesHumanized(marketData: MarketDataType): Promise<EmodeDataHumanized[]> {
    if (Number(marketData.chainId) === 46630) return [];

    const uiPoolDataProvider = await this.getUiPoolDataService(marketData);
    return uiPoolDataProvider.getEModesHumanized({
      lendingPoolAddressProvider: marketData.addresses.LENDING_POOL_ADDRESS_PROVIDER,
    });
  }
}
